import { useEffect, useState, useRef, useCallback } from "react";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";

const FloatingElements = () => {
  const [scrollY, setScrollY] = useState(0);
  const prefersReducedMotion = usePrefersReducedMotion();
  const rafRef = useRef<number>(0);

  const handleScroll = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      setScrollY(window.scrollY);
    });
  }, []);

  useEffect(() => {
    if (prefersReducedMotion) return;

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [prefersReducedMotion, handleScroll]);

  // Calculate opacity based on scroll (fades out gradually over 3000px)
  const getOpacity = (baseOpacity: number) => {
    const fadeStart = 0;
    const fadeEnd = 3000;
    const fadeProgress = Math.min(Math.max((scrollY - fadeStart) / (fadeEnd - fadeStart), 0), 1);
    return baseOpacity * (1 - fadeProgress);
  };

  if (prefersReducedMotion) {
    return (
      <div className="absolute inset-0 pointer-events-none overflow-hidden z-0" aria-hidden="true">
        <div className="absolute top-20 left-10 text-5xl opacity-60">☁️</div>
        <div className="absolute top-40 right-20 text-6xl opacity-50">☁️</div>
        <div className="absolute top-60 left-1/4 text-5xl opacity-50">☁️</div>
        <div className="absolute bottom-1/3 right-1/4 text-6xl opacity-50">☁️</div>
      </div>
    );
  }

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden z-0" aria-hidden="true">
      {/* Clouds with smooth parallax effect */}
      <div
        className="absolute top-20 left-10 text-5xl transition-all duration-300 ease-out"
        style={{
          transform: `translateY(${scrollY * 0.72}px)`,
          opacity: getOpacity(0.6),
        }}
      >
        ☁️
      </div>
      <div
        className="absolute top-40 right-20 text-6xl transition-all duration-300 ease-out"
        style={{
          transform: `translateY(${scrollY * 0.63}px)`,
          opacity: getOpacity(0.5),
        }}
      >
        ☁️
      </div>
      <div
        className="absolute top-60 left-1/4 text-5xl transition-all duration-300 ease-out"
        style={{
          transform: `translateY(${scrollY * 0.425}px)`,
          opacity: getOpacity(0.5),
        }}
      >
        ☁️
      </div>
      <div
        className="absolute bottom-1/3 right-1/4 text-6xl transition-all duration-300 ease-out"
        style={{
          transform: `translateY(${scrollY * 0.235}px)`,
          opacity: getOpacity(0.5),
        }}
      >
        ☁️
      </div>
    </div>
  );
};

export default FloatingElements;
